import {
  ArchetypeScores,
  Option,
  Question,
  TestResult,
  DomainScore,
  ValueDomain,
} from "../types";
import { archetypes } from "../data/archetypes";
import { validateScores } from "./validation";

export const initializeScores = (): ArchetypeScores => ({
  jaguar: 0,
  llama: 0,
  quetzal: 0,
  colibri: 0,
  coqui: 0,
  buho: 0,
  condor: 0,
});

export const updateScores = (
  currentScores: ArchetypeScores,
  option: Option
): ArchetypeScores => {
  if (!option?.scores) {
    console.warn("Invalid option scores");
    return { ...currentScores };
  }

  const newScores = { ...currentScores };

  Object.entries(option.scores).forEach(([key, value]) => {
    const archetypeKey = key as keyof ArchetypeScores;
    if (typeof value === "number" && !isNaN(value)) {
      newScores[archetypeKey] = (newScores[archetypeKey] || 0) + value;
    }
  });

  return newScores;
};

export const calculatePercentages = (
  scores: ArchetypeScores,
  questions: Question[]
): ArchetypeScores => {
  if (!scores || !questions?.length) {
    console.warn("Invalid scores or questions");
    return {
      jaguar: 0,
      llama: 0,
      quetzal: 0,
      colibri: 0,
      coqui: 0,
      buho: 0,
      condor: 0,
    };
  }

  const MAX_SCORE_PER_QUESTION = 5;
  const totalMaxScore = questions.length * MAX_SCORE_PER_QUESTION;

  const percentages: ArchetypeScores = {
    jaguar: 0,
    llama: 0,
    quetzal: 0,
    colibri: 0,
    coqui: 0,
    buho: 0,
    condor: 0,
  };

  Object.entries(scores).forEach(([archetype, score]) => {
    if (
      typeof score === "number" &&
      !isNaN(score) &&
      archetype in percentages
    ) {
      const percentage = Math.min(
        100,
        Math.max(0, (score / totalMaxScore) * 100)
      );
      (percentages as any)[archetype] = Number(percentage.toFixed(2));
    }
  });

  return percentages;
};

export const calculateResult = (
  scores: ArchetypeScores,
  questions: Question[],
  selectedOptions: Option[]
): TestResult => {
  if (!scores || !questions?.length || !selectedOptions?.length) {
    throw new Error(
      "Invalid input: scores, questions, and selectedOptions are required"
    );
  }

  try {
    // Validate scores
    const validation = validateScores(scores);
    if (!validation.isValid) {
      throw new Error(`Invalid scores: ${validation.errors.join(", ")}`);
    }

    const percentages = calculatePercentages(scores, questions);

    // Sort archetypes by percentage score
    const sortedArchetypes = Object.entries(percentages).sort(
      ([, a], [, b]) => b - a
    );

    const primaryArchetypeId = sortedArchetypes[0]?.[0];
    const secondaryArchetypeId = sortedArchetypes[1]?.[0];

    if (!primaryArchetypeId || !secondaryArchetypeId) {
      throw new Error("Could not determine archetype rankings");
    }

    const primaryArchetype = archetypes.find(
      (a) => a.id === primaryArchetypeId
    );
    const secondaryArchetype = archetypes.find(
      (a) => a.id === secondaryArchetypeId
    );

    if (!primaryArchetype || !secondaryArchetype) {
      throw new Error("Could not find archetype definitions");
    }

    return {
      primaryArchetype,
      secondaryArchetype,
      scores: percentages,
    };
  } catch (error) {
    console.error("Error calculating result:", error);
    throw error;
  }
};

export const calculateDomainScores = (
  questions: Question[],
  selectedOptions: Option[]
): DomainScore[] => {
  const domains: ValueDomain[] = [
    "family",
    "group_loyalty",
    "humility",
    "sacrifice",
    "respect",
    "communication",
    "authority",
    "tenacity_drive",
    "growth",
    "cultural_pride",
  ];

  const domainScores = domains.reduce((acc, domain) => {
    acc[domain] = { score: 0, maxScore: 0 };
    return acc;
  }, {} as Record<ValueDomain, { score: number; maxScore: number }>);

  questions.forEach((question, index) => {
    if (!question?.category || !domainScores[question.category]) return;

    const domain = question.category;
    const selectedOption = selectedOptions[index];

    if (selectedOption?.scores) {
      const optionScore = Object.values(selectedOption.scores)
        .filter((score) => typeof score === "number" && !isNaN(score))
        .reduce((sum, score) => sum + Math.max(0, score), 0);

      domainScores[domain].score += optionScore;

      // Calculate max possible score for this question
      const maxQuestionScore = Math.max(
        ...question.options.map((opt) =>
          Object.values(opt.scores || {})
            .filter((score) => typeof score === "number" && !isNaN(score))
            .reduce((sum, score) => sum + Math.max(0, score), 0)
        )
      );

      domainScores[domain].maxScore += maxQuestionScore;
    }
  });

  return Object.entries(domainScores).map(([domain, scores]) => {
    const typedScores = scores as { score: number; maxScore: number };
    return {
      domain: domain as ValueDomain,
      score: typedScores.score,
      maxScore: Math.max(1, typedScores.maxScore),
      percentage: Number(
        ((typedScores.score / Math.max(1, typedScores.maxScore)) * 100).toFixed(
          2
        )
      ),
    };
  });
};
